import {
  addDoc,
  collection,
  doc,
  getDoc,
  limit,
  onSnapshot,
  query,
  serverTimestamp,
  setDoc,
  where,
} from "firebase/firestore";
import { getFirebaseWebFirestore } from "./firebaseWeb";

const THREADS_COLLECTION = "webMessageThreads";
const MESSAGES_COLLECTION = "messages";
const FELIX_UID = "felix-system";
const FELIX_PROFILE = {
  uid: FELIX_UID,
  displayName: "Felix",
  nickname: "felix",
  profileTitle: "DenkUP Yardımcı Asistanı",
  level: 1,
  initials: "FX",
};
const FELIX_WELCOME_MESSAGE = "DenkUP'a hoş geldin! Ben yardımcı Felix. İlk adım olarak telefon doğrulamanı tamamlamanı, ardından profil bilgilerini ve ilgi alanlarını güncellemeni öneririm.";

function cleanText(value) {
  return String(value || "").trim();
}

function initialsFromName(value) {
  return cleanText(value || "DU")
    .split(/\s+/g)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toLocaleUpperCase("tr-TR"))
    .join("");
}

function timestampMs(value) {
  const date = value?.toDate?.() || (value ? new Date(value) : null);
  return date && Number.isFinite(date.getTime()) ? date.getTime() : 0;
}

function threadIdFor(leftUid, rightUid) {
  return [cleanText(leftUid), cleanText(rightUid)].filter(Boolean).sort().join("__");
}

function normalizeParticipant(input = {}) {
  const displayName = cleanText(input.displayName || input.name || input.nickname || input.email || input.uid);
  const nickname = cleanText(input.nickname || input.handle).replace(/^@+/, "");
  return {
    uid: cleanText(input.uid),
    displayName,
    nickname,
    profileTitle: cleanText(input.profileTitle || input.title || input.roleTitle),
    level: Number(input.level || input.levelProfile?.level || 0),
    profileImage: input.profileImage || null,
    initials: input.profileImage?.initials || initialsFromName(displayName || nickname),
  };
}

function normalizeThread(snapshot, currentUid) {
  const data = snapshot.data() || {};
  const participants = data.participants || {};
  const participantUids = Array.isArray(data.participantUids) ? data.participantUids : [];
  const otherUid = participantUids.find((uid) => uid !== currentUid) || "";
  const other = participants[otherUid] || {};
  return {
    id: snapshot.id,
    ...data,
    participantUids,
    otherUid,
    other: normalizeParticipant({ uid: otherUid, ...other }),
    lastMessageAtMs: timestampMs(data.lastMessageAt || data.updatedAt || data.createdAt),
  };
}

function normalizeMessage(snapshot) {
  const data = snapshot.data() || {};
  return {
    id: snapshot.id,
    ...data,
    createdAtMs: timestampMs(data.createdAt),
    body: cleanText(data.body),
  };
}

export function listenMessageThreads(user, onChange, onError) {
  if (!user?.uid) {
    onChange?.([]);
    return () => {};
  }
  const db = getFirebaseWebFirestore();
  const q = query(
    collection(db, THREADS_COLLECTION),
    where("participantUids", "array-contains", user.uid),
    limit(50),
  );
  return onSnapshot(q, (snapshot) => {
    const rows = snapshot.docs
      .map((entry) => normalizeThread(entry, user.uid))
      .sort((left, right) => right.lastMessageAtMs - left.lastMessageAtMs);
    onChange?.(rows);
  }, (error) => {
    onError?.(error);
  });
}

export function listenThreadMessages(threadId, onChange, onError) {
  const safeThreadId = cleanText(threadId);
  if (!safeThreadId) {
    onChange?.([]);
    return () => {};
  }
  const db = getFirebaseWebFirestore();
  const q = query(
    collection(db, THREADS_COLLECTION, safeThreadId, MESSAGES_COLLECTION),
    limit(150),
  );
  return onSnapshot(q, (snapshot) => {
    const rows = snapshot.docs
      .map(normalizeMessage)
      .sort((left, right) => left.createdAtMs - right.createdAtMs);
    onChange?.(rows);
  }, (error) => {
    onError?.(error);
  });
}

export async function ensureMessageThread({ user, currentProfile, targetProfile }) {
  if (!user?.uid) throw new Error("Mesajlaşmak için giriş yapmalısın.");
  const target = normalizeParticipant(targetProfile);
  if (!target.uid) throw new Error("Mesaj gönderilecek kullanıcı bulunamadı.");
  if (target.uid === user.uid) throw new Error("Kendi profiline mesaj gönderemezsin.");

  const current = normalizeParticipant({
    uid: user.uid,
    email: user.email,
    displayName: currentProfile?.displayName || user.displayName || currentProfile?.nickname || user.email,
    nickname: currentProfile?.nickname,
    profileTitle: currentProfile?.profileTitle || currentProfile?.title,
    level: currentProfile?.level,
    profileImage: currentProfile?.profileImage,
  });
  const threadId = threadIdFor(user.uid, target.uid);
  const db = getFirebaseWebFirestore();
  const ref = doc(db, THREADS_COLLECTION, threadId);
  const snapshot = await getDoc(ref);
  const now = serverTimestamp();
  const participants = {
    [current.uid]: current,
    [target.uid]: target,
  };

  await setDoc(ref, {
    threadId,
    participantUids: [current.uid, target.uid].sort(),
    participants,
    updatedAt: now,
    ...(snapshot.exists() ? {} : { createdAt: now, lastMessageAt: now, lastMessagePreview: "" }),
  }, { merge: true });

  return {
    id: threadId,
    threadId,
    participantUids: [current.uid, target.uid].sort(),
    participants,
    otherUid: target.uid,
    other: target,
  };
}

export async function ensureFelixWelcomeThread({ user, currentProfile } = {}) {
  if (!user?.uid) return null;
  const current = normalizeParticipant({
    uid: user.uid,
    email: user.email,
    displayName: currentProfile?.displayName || user.displayName || currentProfile?.nickname || user.email,
    nickname: currentProfile?.nickname,
    profileTitle: currentProfile?.profileTitle || currentProfile?.title,
    level: currentProfile?.level,
    profileImage: currentProfile?.profileImage,
  });
  const target = normalizeParticipant(FELIX_PROFILE);
  const threadId = threadIdFor(current.uid, target.uid);
  const db = getFirebaseWebFirestore();
  const ref = doc(db, THREADS_COLLECTION, threadId);
  const snapshot = await getDoc(ref);
  if (snapshot.exists()) return normalizeThread(snapshot, user.uid);

  const now = serverTimestamp();
  const participants = {
    [current.uid]: current,
    [target.uid]: target,
  };
  await setDoc(ref, {
    threadId,
    participantUids: [current.uid, target.uid].sort(),
    participants,
    createdAt: now,
    updatedAt: now,
    lastMessageAt: now,
    lastMessagePreview: FELIX_WELCOME_MESSAGE,
    lastSenderUid: FELIX_UID,
  });
  await addDoc(collection(db, THREADS_COLLECTION, threadId, MESSAGES_COLLECTION), {
    threadId,
    senderUid: FELIX_UID,
    body: FELIX_WELCOME_MESSAGE,
    createdAt: now,
    immutable: true,
    system: true,
  });
  return {
    id: threadId,
    threadId,
    participantUids: [current.uid, target.uid].sort(),
    participants,
    otherUid: target.uid,
    other: target,
  };
}

export async function sendMessage({ user, thread, body }) {
  if (!user?.uid) throw new Error("Mesaj göndermek için giriş yapmalısın.");
  const safeBody = cleanText(body);
  if (!safeBody) throw new Error("Mesaj boş olamaz.");
  if (safeBody.length > 1200) throw new Error("Mesaj en fazla 1200 karakter olabilir.");
  const threadId = cleanText(thread?.id || thread?.threadId);
  if (!threadId) throw new Error("Mesaj konuşması bulunamadı.");

  const db = getFirebaseWebFirestore();
  const now = serverTimestamp();
  await addDoc(collection(db, THREADS_COLLECTION, threadId, MESSAGES_COLLECTION), {
    threadId,
    senderUid: user.uid,
    body: safeBody,
    createdAt: now,
    immutable: true,
  });
  await setDoc(doc(db, THREADS_COLLECTION, threadId), {
    lastMessageAt: now,
    lastMessagePreview: safeBody.slice(0, 140),
    lastSenderUid: user.uid,
    updatedAt: now,
  }, { merge: true });
}
